import React from "react";
import { Routes } from "@routes";
import { Link } from "react-router-dom";
import { PhoneCall, Car, Receipt, FileText, Leaf, BarChart2, Scale, Bot, MonitorSmartphone, Check, Zap, MapPin } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../../lib/utils";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import NoAuth from "../no-auth";

// UI Components
// Badge Component
const badgeVariants = cva(
    "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
    {
        variants: {
            variant: {
                default: "border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80",
                secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
                destructive: "border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
                outline: "text-foreground",
            },
        },
        defaultVariants: {
            variant: "default",
        },
    }
);

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> { }

function Badge({ className, variant, ...props }: BadgeProps) {
    return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

// Card Component
interface CardProps extends React.HTMLAttributes<HTMLDivElement> { }

function Card({ className, ...props }: CardProps) {
    return <div className={cn("rounded-lg border bg-card text-card-foreground shadow-sm", className)} {...props} />;
}

function CardHeader({ className, ...props }: CardProps) {
    return <div className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} />;
}

function CardTitle({ className, children, ...props }: CardProps) {
    return <h3 className={cn("text-2xl font-semibold leading-none tracking-tight", className)} {...props}>{children}</h3>;
}

function CardDescription({ className, ...props }: CardProps) {
    return <p className={cn("text-sm text-muted-foreground", className)} {...props} />;
}

function CardContent({ className, ...props }: CardProps) {
    return <div className={cn("p-6 pt-0", className)} {...props} />;
}

// Button Component
const buttonVariants = cva(
    "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
    {
        variants: {
            variant: {
                default: "bg-primary text-primary-foreground hover:bg-primary/90",
                destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
                outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
                secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
                ghost: "hover:bg-accent hover:text-accent-foreground",
                link: "text-primary underline-offset-4 hover:underline",
            },
            size: {
                default: "h-10 px-4 py-2",
                sm: "h-9 rounded-md px-3",
                lg: "h-11 rounded-md px-8",
                icon: "h-10 w-10",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
);

// Feature component for pricing cards
type FeatureProps = {
    title: string;
    description: string;
    icon: React.ElementType;
    index?: number;
    iconClassName?: string;
};

const Feature = ({ title, description, icon: Icon, index = 0, iconClassName }: FeatureProps) => (
    <motion.div
        className="flex flex-row gap-4"
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, delay: 0.1 + (index * 0.05) }}
    >
        <Icon className={cn("w-5 h-5 mt-1 text-primary", iconClassName)} />
        <div className="flex flex-col">
            <p>{title}</p>
            <p className="text-muted-foreground text-sm">{description}</p>
        </div>
    </motion.div>
);

// Plan type definition
type PricingPlan = {
    name: string;
    description: string;
    price: string;
    currency: string;
    frequency: string;
    setupFee: string;
    features: FeatureProps[];
    buttonText?: string;
    buttonVariant?: "default" | "outline";
    path: string;
    setupFeeLabel?: string;
};

// Reusable PricingCard component
const PricingCard = ({
    name,
    description,
    price,
    currency,
    frequency,
    setupFee,
    features,
    buttonText,
    buttonVariant = "outline",
    path,
    setupFeeLabel
}: PricingPlan) => {
    const { t } = useTranslation();
    const isAllInOne = name === t('pricing.plans.allInOne.name');

    const policyFeature = features.find(feature => feature.title === t('pricing.plans.timeRegistration.features.timePolicy.title'));
    const regularFeatures = policyFeature ? features.filter(feature => feature.title !== t('pricing.plans.timeRegistration.features.timePolicy.title')) : features;

    return (
        <Card className={cn(
            "w-full h-full min-h-[22rem] rounded-md flex flex-col justify-between",
            isAllInOne && "border-primary shadow-lg scale-105 bg-primary/5"
        )}>
            <div>
                <CardHeader>
                    <CardTitle>
                        <span className="flex flex-row gap-4 items-center font-normal">
                            {name}
                            {isAllInOne && <Badge variant="default" className="text-white">{t('pricing.plans.allInOne.popular')}</Badge>}
                        </span>
                    </CardTitle>
                    <CardDescription>{description}</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-col gap-8">
                        <div className="flex flex-col gap-1">
                            <p className="text-sm text-muted-foreground mb-1">
                                {t('pricing.price')}
                            </p>
                            <p className="flex flex-row items-baseline gap-2 text-xl">
                                <span className="text-4xl">{price} {currency}</span>
                                <span className="text-sm text-muted-foreground">{frequency}</span>
                            </p>
                            <p className="text-base flex items-center gap-1">
                                <span className="font-semibold text-primary text-lg">{setupFee} {currency}</span>
                                <span className="text-muted-foreground">{setupFeeLabel || t('pricing.setupFee')}</span>
                            </p>
                        </div>
                        <div className="flex flex-col gap-4">
                            {regularFeatures.map((feature, idx) => (
                                <Feature key={idx} index={idx} title={feature.title} description={feature.description} icon={feature.icon} />
                            ))}

                            {policyFeature && (
                                <motion.div
                                    className="flex flex-row gap-4"
                                    initial={{ opacity: 0, y: 8 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: 0.1 + (regularFeatures.length * 0.05) }}
                                >
                                    <Check className="w-4 h-4 mt-2 text-primary" />
                                    <div className="flex flex-col">
                                        <p>{t('pricing.plans.timeRegistration.features.timePolicy.title')}</p>
                                        <div className="flex flex-row items-center gap-1 text-sm text-muted-foreground">
                                            <span className="font-semibold text-primary">495 {currency}</span>
                                            <span>{t('pricing.perContract')}</span>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </div>
                    </div>
                </CardContent>
            </div>

            <div className="p-6">
                <Link
                    to={path}
                    className={cn(
                        buttonVariants({ variant: buttonVariant }),
                        "gap-4 flex items-center justify-center w-full"
                    )}
                >
                    {buttonText || t('pricing.bookMeeting')} <PhoneCall className="w-4 h-4" />
                </Link>
            </div>
        </Card>
    );
};

// Main Full Pricing Component
const FullPricingContent = () => {
    const { t } = useTranslation();

    // Define all pricing plans with translation keys
    const pricingPlans: PricingPlan[] = [
        {
            name: t('pricing.plans.timeRegistration.name'),
            description: t('pricing.plans.timeRegistration.description'),
            price: "29",
            currency: "DKK",
            frequency: t('pricing.perUserPerMonth'),
            setupFee: "995",
            features: [
                {
                    title: t('pricing.plans.timeRegistration.features.legalCompliance.title'),
                    description: t('pricing.plans.timeRegistration.features.legalCompliance.description'),
                    icon: Scale
                },
                {
                    title: t('pricing.plans.timeRegistration.features.automaticTracking.title'),
                    description: t('pricing.plans.timeRegistration.features.automaticTracking.description'),
                    icon: Bot
                },
                {
                    title: t('pricing.plans.timeRegistration.features.userFriendly.title'),
                    description: t('pricing.plans.timeRegistration.features.userFriendly.description'),
                    icon: MonitorSmartphone
                },
                {
                    title: t('pricing.plans.timeRegistration.features.timePolicy.title'),
                    description: t('pricing.plans.timeRegistration.features.timePolicy.description'),
                    icon: Check
                },
            ],
            path: Routes.ContactSales.path
        },
        {
            name: t('pricing.plans.drivingAllowance.name'),
            description: t('pricing.plans.drivingAllowance.description'),
            price: "9",
            currency: "DKK",
            frequency: t('pricing.perUserPerMonth'),
            setupFee: "0",
            features: [
                {
                    title: t('pricing.plans.drivingAllowance.features.gpsTracking.title'),
                    description: t('pricing.plans.drivingAllowance.features.gpsTracking.description'),
                    icon: Car
                },
                {
                    title: t('pricing.plans.drivingAllowance.features.taxCompliance.title'),
                    description: t('pricing.plans.drivingAllowance.features.taxCompliance.description'),
                    icon: FileText
                },
                {
                    title: t('pricing.plans.drivingAllowance.features.accounting.title'),
                    description: t('pricing.plans.drivingAllowance.features.accounting.description'),
                    icon: Receipt
                },
                {
                    title: t('pricing.plans.drivingAllowance.features.dashboard.title'),
                    description: t('pricing.plans.drivingAllowance.features.dashboard.description'),
                    icon: BarChart2
                },
            ],
            path: Routes.ContactSales.path
        },
        {
            name: t('pricing.plans.lcaA4.name'),
            description: t('pricing.plans.lcaA4.description'),
            price: t('pricing.custom'),
            currency: "",
            frequency: t('pricing.perProject'),
            setupFee: "—",
            features: [
                {
                    title: t('pricing.plans.lcaA4.features.transportModes.title'),
                    description: t('pricing.plans.lcaA4.features.transportModes.description'),
                    icon: Car
                },
                {
                    title: t('pricing.plans.lcaA4.features.payloadUtil.title'),
                    description: t('pricing.plans.lcaA4.features.payloadUtil.description'),
                    icon: Scale
                },
                {
                    title: t('pricing.plans.lcaA4.features.fuel.title'),
                    description: t('pricing.plans.lcaA4.features.fuel.description'),
                    icon: Leaf
                },
                {
                    title: t('pricing.plans.lcaA4.features.reporting.title'),
                    description: t('pricing.plans.lcaA4.features.reporting.description'),
                    icon: BarChart2
                }
            ],
            buttonText: t('pricing.contactSales'),
            path: Routes.ContactSales.path
        },
        {
            name: t('pricing.plans.packaging.name'),
            description: t('pricing.plans.packaging.description'),
            price: "4.995",
            currency: "DKK",
            frequency: t('pricing.perYear'),
            setupFee: "0",
            features: [
                {
                    title: t('pricing.plans.packaging.features.components.title'),
                    description: t('pricing.plans.packaging.features.components.description'),
                    icon: MonitorSmartphone
                },
                {
                    title: t('pricing.plans.packaging.features.co2Factors.title'),
                    description: t('pricing.plans.packaging.features.co2Factors.description'),
                    icon: Leaf
                },
                {
                    title: t('pricing.plans.packaging.features.epr.title'),
                    description: t('pricing.plans.packaging.features.epr.description'),
                    icon: Scale
                },
                {
                    title: t('pricing.plans.packaging.features.reporting.title'),
                    description: t('pricing.plans.packaging.features.reporting.description'),
                    icon: BarChart2
                }
            ],
            buttonText: t('pricing.contactSales'),
            path: Routes.ContactSales.path
        },
        {
            name: t('pricing.plans.scope12.name'),
            description: t('pricing.plans.scope12.description'),
            price: "600",
            currency: "DKK",
            frequency: t('pricing.perMonth'),
            setupFee: "0",
            features: [
                {
                    title: t('pricing.plans.scope12.features.autoCalc.title'),
                    description: t('pricing.plans.scope12.features.autoCalc.description'),
                    icon: Zap
                },
                {
                    title: t('pricing.plans.scope12.features.municipalFactors.title'),
                    description: t('pricing.plans.scope12.features.municipalFactors.description'),
                    icon: MapPin
                },
                {
                    title: t('pricing.plans.scope12.features.pdfUpload.title'),
                    description: t('pricing.plans.scope12.features.pdfUpload.description'),
                    icon: FileText
                },
                {
                    title: t('pricing.plans.scope12.features.dashboard.title'),
                    description: t('pricing.plans.scope12.features.dashboard.description'),
                    icon: BarChart2
                }
            ],
            buttonText: t('pricing.contactSales'),
            path: Routes.ContactSales.path
        },
    ];

    // Separate plans into groups
    // Time Registration, Driving Allowance
    const coreServicePlans = [
        pricingPlans[0],
        pricingPlans[1],
    ];

    // LCA A4, Packaging, Scope 1 & 2
    const customSolutionPlans = [
        pricingPlans[2],
        pricingPlans[3],
        pricingPlans[4]
    ];

    return (
        <div id="pricing" className="bg-secBg w-full py-24">
            <div className="px-4 mx-auto max-w-screen-xl">
                <div className="flex text-center justify-center items-center gap-4 flex-col">
                    <motion.div
                        className="w-full max-w-3xl mx-auto"
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.1 }}
                        transition={{ duration: 0.45 }}
                    >
                        <Badge variant="outline" className="mb-4">
                            {t('pricing.badge')}
                        </Badge>
                        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight">
                            {t('pricing.title')}
                        </h1>
                        <p className="mt-3 text-muted-foreground text-lg max-w-2xl mx-auto">
                            {t('pricing.subtitle')}
                        </p>
                    </motion.div>

                    {/* Core Services Section */}
                    <motion.div
                        className="w-full max-w-2xl mx-auto pt-8"
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.1 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        <div className="text-center mb-12">
                            <h3 className="text-3xl font-semibold mb-2">
                                {t('pricing.groups.coreServices.title')}
                            </h3>
                            <p className="text-muted-foreground max-w-2xl mx-auto">
                                {t('pricing.groups.coreServices.description')}
                            </p>
                        </div>
                        <div className="grid text-left grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 auto-rows-fr">
                            {coreServicePlans.map((plan, index) => (
                                <motion.div
                                    className="h-full"
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.1 }}
                                    transition={{ duration: 0.45, delay: 0.3 + (index * 0.1) }}
                                >
                                    <PricingCard {...plan} />
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    {/* Custom Solutions Section */}
                    <motion.div
                        className="w-full max-w-5xl mx-auto pt-20"
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.1 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        <div className="text-center mb-12">
                            <h3 className="text-3xl font-semibold mb-2">
                                {t('pricing.groups.customSolutions.title')}
                            </h3>
                            <p className="text-muted-foreground max-w-2xl mx-auto">
                                {t('pricing.groups.customSolutions.description')}
                            </p>
                        </div>
                        <div className="grid text-left grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 auto-rows-fr">
                            {customSolutionPlans.map((plan, index) => (
                                <motion.div
                                    className="h-full"
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.1 }}
                                    transition={{ duration: 0.45, delay: 0.3 + (index * 0.1) }}
                                >
                                    <Card className="w-full h-full min-h-[22rem] rounded-md flex flex-col justify-between">
                                        <div>
                                            <CardHeader>
                                                <CardTitle>
                                                    <span className="flex flex-row gap-4 items-center font-normal">
                                                        {plan.name}
                                                    </span>
                                                </CardTitle>
                                                <CardDescription>{plan.description}</CardDescription>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="flex flex-col gap-8">
                                                    <div className="flex flex-col gap-1">
                                                        <p className="text-sm text-muted-foreground mb-1">
                                                            {t('pricing.price')}
                                                        </p>
                                                        <p className="flex flex-row items-baseline gap-2 text-xl">
                                                            <span className="text-4xl font-semibold">{plan.price} {plan.currency}</span>
                                                            <span className="text-sm text-muted-foreground">{plan.frequency}</span>
                                                        </p>
                                                    </div>
                                                    <div className="flex flex-col gap-4">
                                                        {plan.features.slice(0, 3).map((feature, idx) => (
                                                            <Feature key={idx} index={idx} title={feature.title} description={feature.description} icon={feature.icon} iconClassName="w-5 h-5 mt-1" />
                                                        ))}
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </div>
                                        <div className="p-6">
                                            <Link
                                                to={plan.path}
                                                className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 gap-4 flex w-full"
                                            >
                                                {plan.buttonText || t('pricing.contactSales')}
                                            </Link>
                                        </div>
                                    </Card>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

const FullPricing = () => {
    const { t } = useTranslation();

    return (
        <NoAuth
            title={t('pricing.pageTitle', { defaultValue: 'Pricing' })}
            component={FullPricingContent}
        />
    );
};

export default FullPricing;


